import { getSessionUniversal } from "@/app/access/session";
import { ClientAuthProvider } from "@crm/access/auth/session-auth";
import { pendingAuthContext } from "@crm/access/auth/state";
import type { AuthContext, RouterContext } from "@crm/access/types/router";
import { RegistryProvider } from "@effect/atom-react";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import { getErrorStatusCode } from "@crm/errors";
import { serverLogger } from "@crm/errors/server-logger";
import { Sentry } from "@crm/shared/sentry";
import appCss from "@crm/ui/styles/tokens.css?url";
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { Data, Effect } from "effect";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { DefaultErrorComponent } from "../components/error-boundary";
import { NotFound } from "../components/not-found";
import { authContextFromSsrSessionResult, type SsrSessionResult } from "../lib/ssr-auth-state";
import { initWebVitals } from "../lib/web-vitals/report";

declare const __E2E_LIGHTPANDA__: boolean;

type RootBeforeLoadResult = {
  auth: AuthContext;
};

class RootRouteWorkflowError extends Data.TaggedError("RootRouteWorkflowError")<{
  readonly message: string;
  readonly cause?: unknown;
}> {}

function toRootRouteWorkflowError(cause: unknown, fallbackMessage: string): RootRouteWorkflowError {
  return new RootRouteWorkflowError({
    message: cause instanceof Error ? cause.message : fallbackMessage,
    cause,
  });
}

function promiseRootRouteEffect<A>(
  run: () => A | PromiseLike<A>,
  fallbackMessage: string,
): Effect.Effect<Awaited<A>, RootRouteWorkflowError> {
  return Effect.tryPromise({
    try: () => Promise.resolve(run()),
    catch: (cause) => toRootRouteWorkflowError(cause, fallbackMessage),
  });
}

function ssrAuthBeforeLoadEffect(): Effect.Effect<RootBeforeLoadResult> {
  return Effect.gen(function* () {
    const result: SsrSessionResult = yield* promiseRootRouteEffect(
      () =>
        typeof window === "undefined"
          ? import("@tanstack/react-start/server").then(({ getRequest }) =>
              getSessionUniversal(getRequest().headers),
            )
          : getSessionUniversal(),
      "SSR session lookup failed",
    ).pipe(
      Effect.match({
        onFailure: (error): SsrSessionResult => ({
          _tag: "Failure",
          message: error.message,
        }),
        onSuccess: (loadedSession): SsrSessionResult => ({
          _tag: "Success",
          session: loadedSession,
        }),
      }),
      Effect.timeoutOrElse({
        duration: "5 seconds",
        orElse: () => Effect.succeed<SsrSessionResult>({ _tag: "Timeout" }),
      }),
    );

    const auth = authContextFromSsrSessionResult(result);
    if (auth.status === "pending" || auth.status === "failed") {
      yield* Effect.sync(() =>
        serverLogger.warn("SSR session unavailable — falling back to client auth", {
          authStatus: auth.status,
          didTimeout: result._tag === "Timeout",
        }),
      );
    }
    return { auth };
  });
}

function rootBeforeLoadEffect(): Effect.Effect<RootBeforeLoadResult> {
  if (typeof window === "undefined") {
    return ssrAuthBeforeLoadEffect();
  }

  return Effect.succeed({ auth: pendingAuthContext() });
}

/**
 * Root Route with Shell Component Pattern
 *
 * 优化策略：
 * - shellComponent: 始终在服务器端渲染 HTML shell (<html>, <body>, <Scripts>)
 * - component: 可以在客户端渲染，避免 SSR 开销
 * - ssr: false - 主组件在客户端渲染，提升性能
 * - beforeLoad: 服务器端查询 DB，客户端使用 Better Auth 缓存
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  // beforeLoad - SSR 只确认 session，不读取用户表或权限表
  beforeLoad: () => browserEffectRuntime.runPromise(rootBeforeLoadEffect()),

  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "imiker CRM",
      },
      {
        name: "google",
        content: "notranslate",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),

  // Shell Component: 始终 SSR，提供 HTML 基础结构
  shellComponent: RootShell,

  // Main Component: 客户端渲染，包含应用逻辑
  component: RootComponent,
  // Production and Chrome E2E keep the CSR shell. Lightpanda 0.3.7 cannot
  // execute this Vite/TanStack client module graph reliably, so only its
  // low-cost suite renders the authenticated app tree on the server.
  ssr: typeof __E2E_LIGHTPANDA__ !== "undefined" && __E2E_LIGHTPANDA__,

  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
});

/**
 * rolldown 压缩辅助 `__name`（keepNames）在 Cloudflare Workers 运行时
 * 的 Function.prototype.toString() 输出中保留（Node 不会）。seroval 序列化
 * ReadableStream 时把该函数源码嵌入 SSR 内联脚本，浏览器执行时 `__name`
 * 未定义 → ReferenceError。此处注入全局定义（与 esbuild/rolldown 实现一致），
 * 必须在 `$tsr` 数据脚本（含序列化代码）之前执行。
 */
const NAME_SHIM_SCRIPT = `function __name(fn,name){try{Object.defineProperty(fn,"name",{value:name,configurable:true})}catch(e){}return fn}`;

/**
 * RootShell Component (始终 SSR)
 *
 * 提供 HTML 基础结构，包括：
 * - <html>, <head>, <body> 标签
 * - <HeadContent> - meta 标签和 CSS
 * - <Scripts> - 客户端 hydration 脚本
 *
 * 这个组件始终在服务器端渲染，确保浏览器收到完整的 HTML
 */
function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="zh-CN" translate="no">
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {/* 静态常量脚本（无用户输入），用于定义 rolldown keepNames 辅助 __name */}
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: static shim, no user input */}
        <script dangerouslySetInnerHTML={{ __html: NAME_SHIM_SCRIPT }} />
        {children}
        <Toaster
          closeButton
          duration={4000}
          position="top-right"
          toastOptions={{
            style: {
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-m)",
              boxShadow: "var(--shadow-pop)",
              color: "var(--ink)",
              fontSize: "13.5px",
            },
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Root Error Component
 */
function RootErrorComponent(props: ErrorComponentProps) {
  // 捕获路由错误并发送到 Sentry
  useEffect(() => {
    const statusCode = getErrorStatusCode(props.error);
    if (props.error && (!statusCode || statusCode >= 500)) {
      Sentry.captureException(props.error);
    }
  }, [props.error]);

  return <DefaultErrorComponent {...props} />;
}

/**
 * Root Not Found Component
 */
function RootNotFoundComponent() {
  return <NotFound />;
}

/**
 * RootComponent (客户端渲染)
 *
 * 性能优化点：
 * - 由于 ssr: false，这个组件只在客户端渲染
 * - 避免了服务器端渲染整个应用树的开销
 * - Header、RegistryProvider 等都在客户端初始化
 *
 * 认证流程：
 * - SSR: 使用 beforeLoad 的认证状态（来自数据库）
 * - 客户端: 使用 Better Auth 的 useSession hook，利用其内置缓存
 */
function RootComponent() {
  const context = Route.useRouteContext();

  return (
    <RegistryProvider>
      <RootApp serverAuth={context.auth} />
    </RegistryProvider>
  );
}

function RootApp({ serverAuth }: { serverAuth: AuthContext }) {
  useEffect(() => {
    initWebVitals();
  }, []);

  return (
    <ClientAuthProvider serverAuth={serverAuth}>
      <Outlet />
    </ClientAuthProvider>
  );
}
